<script>
import { required, requiredIf } from "vuelidate/lib/validators";
import { digitalLoadMethods, digitalLoadComputed, layoutMethods, erpUserRolesMethods, liteEloadDropdownMethods, liteEloadDropdownComputed } from "@/state/helpers";
import Swal from "sweetalert2";
import { getModuleAccess, showAccessRestrictedModal } from "@/router/helper.js";
import Multiselect from "vue-multiselect";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

const carrier = "globe";

const clone = (v) => {
  return JSON.parse(JSON.stringify(v));
};

export default {
  page: { title: "Peddlr Lite Manager - E-Load SKU" },

  components: { Multiselect, flatPickr },

  data() {
    return {
      promoId: 0,

      eload: {
        promoId: "",
        status: 0,
        limitedStatus: 0,

        amount: "",
        service: null,
        network: null,

        category: "",
        categoryIds: [],
        categoryNames: [],

        name: "",
        code: "",

        input_type: null,
        minimum: null,
        maximum: null,

        validity: "",
        description: "",

        error: "",
      },

      submitted: false,

      changeLogModal:false,
      // change preview panel
      changePreviewVisible: false,
      changePreviewData: null,

      // two independent toggles
      promo_amount_mode: "default",
      details_mode: "default",
      originalDetailsMode: null,
      // temporary: show/hide the schedule note (dev toggle)
      showScheduleNote: false,

      // change log modal data
      logFields: [
        { key: 'created_at', label: 'Date & Time', sortable: true },
        { key: 'activity', label: 'Activity', sortable: true },
        { key: 'details', label: 'Details', sortable: false },
        { key: 'created_by', label: 'Created By', sortable: true }
      ],
      searchLogOption: [
        { text: "Search All", value: null },
        { text: "Created By", value: 'created_by' },
      ],
      searchValue: {
        search: null,
        searchField: null,
        dateRange: null,
        page: 1,
        rows: 10
      },
      pageOptions: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
      rangeOptions: {
        mode: 'range',
        dateFormat: 'Y-m-d',
        wrap: false
      },
      sortBy: 'createdAt',
      sortDesc: true,

      // snapshots
      defaultDetails: null,       // default values loaded from DB
      customDetails: null,        // custom JSON details loaded from DB (object)
      customAmount: null,         // custom amount loaded from DB (number)
      defaultCategoryIds: [],
      defaultCategoryNames: [],
      customCategoryIds: [],
      customCategoryNames: [],

      inputTypeHandler: null,

      eloadPromoModule: { access: { create: true, update: true } },

      categoryList: [],
      categoryCache: {},
      categorySelectionCache: {},  // saves per-network category selection so switching back restores it
      lastNetwork: null,           // tracks the last committed network (v-model updates eload.network BEFORE @change fires)
      eloadServiceOptions: [],
      inputTypeList: [
        { value: null, text: "Select Input Type", disabled: true },
        { value: "MN", text: "Mobile Number" },
        { value: "AN", text: "Account Number" },
      ],
    };
  },

  validations: {
    eload: {
      amount: { required },

      // only required when details are editable/custom
      service: { required: requiredIf(function () { return this.details_mode === "custom"; }) },
      network: { required: requiredIf(function () { return this.details_mode === "custom"; }) },
      category: { required: requiredIf(function () { return this.details_mode === "custom"; }) },
      name: { required: requiredIf(function () { return this.details_mode === "custom"; }) },
      code: { required: requiredIf(function () { return this.details_mode === "custom"; }) },
      input_type: { required: requiredIf(function () { return this.details_mode === "custom"; }) },
      validity: { required: requiredIf(function () { return this.details_mode === "custom"; }) },
      description: { required: requiredIf(function () { return this.details_mode === "custom"; }) },
    },
  },

  computed: {
    ...digitalLoadComputed,
    ...liteEloadDropdownComputed,
    selectedCategory: {
      get() {
        const selected = [];
        const addedNames = new Set();
        this.categoryList.forEach((category) => {
          if (this.eload.categoryNames.includes(category.value) && !addedNames.has(category.value)) {
            selected.push(category);
            addedNames.add(category.value);
          }
        });
        return selected.length ? selected : null;
      },
      set(categories) {
        const selectedIds = [];
        const selectedNames = [];

        if (categories) {
          categories.forEach((c) => {
            selectedIds.push(c.id);
            selectedNames.push(c.value);
          });
        }

        this.eload.categoryIds = selectedIds;
        this.eload.categoryNames = selectedNames;

        // keep "category" field consistent with what user sees
        this.eload.category = selectedNames.length ? selectedNames.join(", ") : "";
      },
    },

    networkOptions() {
      return (this.loadNetworkList || []).map((n) => ({
        text: String(n.name || "").trim(),
        value: String(n.name || "").trim(),
      }));
    },

    // Map SKU logs from store
    skuFieldLogs() {
      return (this.$store.state.digitalLoad.skuFieldLogsList || []).map(log => {
        // Transform backend format to frontend format
        const changes = (log.changes || []).map(change => ({
          field: change.field,
          old: change.oldValue,
          new: change.newValue
        }));
        
        return {
          id: log.id,
          created_at: log.created_at,
          activity: log.remarks || 'Update SKU Details',
          created_by: log.actor || 'Unknown',
          changes: changes,
          promoName: log.promoName,
          promoId: log.promoId
        };
      });
    },

    skuFieldLogsTotal() {
      return this.$store.state.digitalLoad?.skuFieldLogsPageInfo?.total || 0;
    },
  },

  mounted: async function () {
    this.promoId = this.$route.params?.id || this.$route.query?.id || this.$route.query?.promoId || 0;

    if (this.promoId == 0) {
      this.$router.push(this.$route.query.redirectFrom || { name: "E-Load Lite SKU Manager" });
      return;
    }

    this.changeTitle({ title: "Edit E-Load SKU" });
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: 'Peddlr', to: { name: 'Home'}},
        { text: 'Peddlr Lite Manager', to: { name: 'E-Load Lite SKU Manager'} },
        { text: 'E-Load SKU', to: { name: 'E-Load Lite SKU Manager'} },
        { text: "E-Load SKU Form", to: { name: "E-Load Lite SKU Form" } },
      ]
    });

    
    await this.getEloadNetworkList({ rows: 99999, order: "ASC" });
    await this.getServices();
    await this.getEloadCategoryList();

    await getModuleAccess("Products", "eLoad", "eLoad Promo").then((access) => (this.eloadPromoModule.access = access));
    await this.editPromo();
  },

  methods: {
    ...layoutMethods,
    ...digitalLoadMethods,
    ...liteEloadDropdownMethods,
    ...erpUserRolesMethods,

    async sortOptions(propName, arr) {
      arr.sort((a, b) => {
        const nameA = String(a[propName] || "").toUpperCase();
        const nameB = String(b[propName] || "").toUpperCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
      return arr;
    },

    async getEloadCategoryList(network = null) {
      const cacheKey = network || '__all__';
      if (this.categoryCache[cacheKey]) {
        this.categoryList = this.categoryCache[cacheKey];
        return;
      }
      try {
        const params = { rows: 9999 };
        if (network) params.network = network;

        const res = await this.fetchLiteNetworkCategoryList(params);
        const rows = (res && res.message && res.message.rows) ? res.message.rows : [];
        const sortedList = await this.sortOptions('categoryName', clone(rows || []));
        const list = sortedList.map((d) => {
          const isSpecial = d.isSpecialCategory == 1 || d.is_special == 1 || d.isSpecial == 1;
          const networkName = d.networkName || d.network || '';
          const display = `${d.categoryName}${networkName ? ' - ' + networkName : ''}${isSpecial ? ' (SC)' : ''}`;
          return {
            text: display,
            value: d.categoryName,
            id: d.categoryId,
            origName: d.categoryName,
            isSpecial: !!isSpecial,
          };
        });
        this.categoryCache[cacheKey] = list;
        this.categoryList = list;
      } catch (e) {
        console.warn('Failed to fetch network category list', e);
      }
    },

    async onNetworkChange(newNetwork) {
      if (this.details_mode !== 'custom') return;

      // lastNetwork is the network that was active BEFORE this @change fired.
      // We cannot use this.eload.network here because v-model already updated it to newNetwork.
      const prevNetwork = this.lastNetwork;
      if (prevNetwork && prevNetwork !== newNetwork) {
        this.categorySelectionCache[prevNetwork] = {
          categoryIds: clone(this.eload.categoryIds),
          categoryNames: clone(this.eload.categoryNames),
        };
      }

      // Load the new network's category list (uses cache, no extra request)
      await this.getEloadCategoryList(newNetwork || null);

      // Restore previously saved selection for this network if it exists,
      // but only keep entries that are actually present in the new category list.
      const prevCount = this.eload.categoryNames.length;
      const saved = newNetwork ? this.categorySelectionCache[newNetwork] : null;
      if (saved && saved.categoryIds.length) {
        const validNames = new Set(this.categoryList.map((c) => c.value));
        const filteredEntries = saved.categoryNames
          .map((name, i) => ({ name, id: saved.categoryIds[i] }))
          .filter((e) => validNames.has(e.name));

        this.eload.categoryIds   = filteredEntries.map((e) => e.id);
        this.eload.categoryNames = filteredEntries.map((e) => e.name);
        this.eload.category      = this.eload.categoryNames.join(', ');

        // Notify about any categories that were dropped
        const dropped = saved.categoryNames.length - filteredEntries.length;
        if (dropped > 0) {
          this.$toastV2.error(`${dropped} categor${dropped > 1 ? 'ies' : 'y'} removed (not available for selected network)`, 3000, 'bottom-right');
        }
      } else {
        // No saved selection — clear so user picks fresh
        if (prevCount > 0) {
          this.$toastV2.error(`${prevCount} categor${prevCount > 1 ? 'ies' : 'y'} removed (not available for selected network)`, 3000, 'bottom-right');
        }
        this.eload.categoryIds = [];
        this.eload.categoryNames = [];
        this.eload.category = '';
      }

      // Update lastNetwork to the new value for the next change event
      this.lastNetwork = newNetwork;
    },

    async onExport(){
      try{
        // ensure logs are up-to-date
        this.searchValue.page = 1;
        await this.fetchSkuFieldLogs();

        const total = this.skuFieldLogsTotal || 0;
        if(total === 0){
          Swal.fire("Failed!", "No data available to export.", "warning");
          return;
        }

        const params = {
          email: JSON.parse(localStorage.getItem('userData')).email,
          page: this.searchValue.page,
          rows: this.searchValue.rows,
          promoId: Number(this.promoId || this.$route.params?.id || this.$route.query?.promoId) || 0,
          sort: this.sortBy,
          orderDesc: this.sortDesc,
          search: this.searchValue.search,
          searchField: this.searchValue.searchField,
          dateRange: this.searchValue.dateRange,
        };

        await this.actionLoadPromo({ action: "export-sku-logs", body: params });

        Swal.fire("Success!", `Your request for reports is pending. It will be sent to your email once it's ready.`, "success");
      }catch(error){
        console.error('Export failed', error);
        Swal.fire("Failed!", `Your request for reports has failed.`, "warning");
      }
    },

    async getServices() {
      await this.actionNetwork({ action: "services", body: {} })
        .then(async (res) => {
          const sortedList = await this.sortOptions("name", res.rows || []);
          this.eloadServiceOptions = sortedList.map((c) => ({ text: c.name, value: c.id }));
        })
        .catch((e) => console.log("services error", e));
    },

    // apply details json to form (does not touch amount)
    async applyCustomDetails(custom) {
      if (!custom || typeof custom !== "object") return;

      this.eload.service = custom.service_id ?? this.eload.service;

      let resolvedNetwork = null;
      if (custom.network_id != null) {
        const net = (this.loadNetworkList || []).find((n) => Number(n.id) === Number(custom.network_id));
        if (net) {
          resolvedNetwork = String(net.name || "").trim();
          this.eload.network = resolvedNetwork;
        }
      }

      // Fetch/restore category list for the custom network (uses cache)
      await this.getEloadCategoryList(resolvedNetwork || this.eload.network || null);

      // Keep lastNetwork in sync so onNetworkChange knows the correct previous value
      this.lastNetwork = this.eload.network;

      this.eload.name = custom.promo_name ?? this.eload.name;
      this.eload.code = custom.promo_code ?? this.eload.code;

      this.eload.input_type = custom.input_type ?? this.eload.input_type;
      this.eload.minimum = custom.min_char ?? this.eload.minimum;
      this.eload.maximum = custom.max_char ?? this.eload.maximum;

      this.eload.validity = custom.validity ?? this.eload.validity;
      this.eload.description = custom.description ?? this.eload.description;
    },

    async editPromo() {
        const data = await this.getEloadPromoDetails({ carrier, body: { promoId: this.promoId } });

        // modes + custom payloads
        this.promo_amount_mode = data.promoAmountMode || "default";
        this.details_mode = data.promoDetailsMode || "default";
        this.originalDetailsMode = data.promoDetailsMode || "default";

        // default fields from DB
        
        this.eload.promoId = data.promoId;
        this.eload.status = data.promoStatus ? 1 : 0;
        this.eload.limitedStatus = data.promoStatus == 2 ? 1 : 0;

        this.eload.amount = data.promoAmount;

        this.eload.service = data.promoServiceId ?? data.service ?? null;
        this.eload.network = data.promoNetwork ?? data.network ?? null;

        const defaultCats = data.promoCategoriesDefault || [];
        const customCats = data.promoCategoriesCustom || [];

        this.defaultCategoryIds = defaultCats.map((c) => c.id);
        this.defaultCategoryNames = defaultCats.map((c) => c.category_name);

        this.customCategoryIds = customCats.map((c) => c.id);
        this.customCategoryNames = customCats.map((c) => c.category_name);

        // seed custom categories (UI fallback) if none exist yet
        if(this.customCategoryIds.length === 0){
          this.customCategoryIds = clone(this.defaultCategoryIds);
          this.customCategoryNames = clone(this.defaultCategoryNames);
        }

        // apply categories based on active details_mode (with fallback)
        if(this.details_mode === 'custom'){
          this.eload.categoryIds = clone(this.customCategoryIds);
          this.eload.categoryNames = clone(this.customCategoryNames);
        }else{
          this.eload.categoryIds = clone(this.defaultCategoryIds);
          this.eload.categoryNames = clone(this.defaultCategoryNames);
        }

        this.eload.category = this.eload.categoryNames.length ? this.eload.categoryNames.join(", ") : "";

        this.eload.name = data.promoName;
        this.eload.code = data.promoCode;

        this.eload.input_type = data.promoInputType ?? data.input_type ?? null;
        this.eload.minimum = data.promoMinChar ?? data.minimum ?? null;
        this.eload.maximum = data.promoMaxChar ?? data.maximum ?? null;

        this.eload.validity = data.promoValidity;
        this.eload.description = data.promoDescription;

        // IMPORTANT: Snapshot base/default values BEFORE applying custom overrides
        // This ensures defaultDetails always contains the true base values from DB
        this.defaultDetails = clone(this.eload);

        this.customAmount = data.customPromoAmount ?? data.custom_promo_amount ?? null;

        const customDetails = data.customDetails || data.custom_details || null;
        this.customDetails = customDetails;

        // NOW apply overrides based on modes (after snapshot)
        if (this.promo_amount_mode === "custom" && this.customAmount != null) {
          this.eload.amount = this.customAmount;
        }

        if (this.details_mode === "custom" && this.customDetails) {
          await this.applyCustomDetails(this.customDetails);
        }

        // Seed lastNetwork so the first onNetworkChange knows what was active on load
        this.lastNetwork = this.eload.network;

        this.submitted = false;
        this.$v.$reset();
    },

    setAmountMode(mode) {
      if (mode !== "default" && mode !== "custom") return;

      this.promo_amount_mode = mode;

      if (!this.defaultDetails) return;

      if (mode === "default") {
        this.eload.amount = this.defaultDetails.amount;
        this.$v.eload.amount.$reset();
        return;
      }

      // custom: use saved custom amount if available, else start from default
      this.eload.amount = this.customAmount != null ? this.customAmount : this.defaultDetails.amount;
      this.$v.eload.amount.$reset();
    },

    async setDetailsMode(mode) {
      if (mode !== "default" && mode !== "custom") return;

      this.details_mode = mode;

      if (!this.defaultDetails) return;

      if (mode === "default") {
        const fields = [
          "service",
          "network",
          "category",
          "categoryIds",
          "categoryNames",
          "name",
          "code",
          "input_type",
          "minimum",
          "maximum",
          "validity",
          "description",
        ];

        fields.forEach((f) => {
          this.eload[f] = clone(this.defaultDetails[f]);
        });
        
        // force default categories from DB mapping (separate from custom)
        this.eload.categoryIds = clone(this.defaultCategoryIds);
        this.eload.categoryNames = clone(this.defaultCategoryNames);
        this.eload.category = this.eload.categoryNames.length ? this.eload.categoryNames.join(", ") : "";

        // Restore full (unfiltered) category list from cache
        await this.getEloadCategoryList();
        // Network reverted to default — sync lastNetwork
        this.lastNetwork = this.eload.network;
        this.inputTypeHandler = null;
        this.submitted = false;
        this.$v.$reset();
        
        return;
      }

      if (mode === "custom") {

        const hasCustomCats = Array.isArray(this.customCategoryIds) && this.customCategoryIds.length > 0;

        // If no custom categories yet, fallback to default categories
        this.eload.categoryIds = clone(hasCustomCats ? this.customCategoryIds : this.defaultCategoryIds);
        this.eload.categoryNames = clone(hasCustomCats ? this.customCategoryNames : this.defaultCategoryNames);
        this.eload.category = this.eload.categoryNames.length ? this.eload.categoryNames.join(", ") : "";

        // apply custom json overrides (does not touch categories)
        if (this.customDetails) {
          await this.applyCustomDetails(this.customDetails);
        }

        // applyCustomDetails may have changed the network — sync lastNetwork
        this.lastNetwork = this.eload.network;
        this.inputTypeHandler = null;
        this.submitted = false;
        this.$v.$reset();
        return;
      }
    },

    onToggleStatus() {
      if (this.eload.status == 0) this.eload.limitedStatus = 0;
    },

    getSelectedInputType() {
      this.inputTypeHandler = null;

      // if (this.eload.input_type === "MN") {
      //   // this.eload.minimum = null;
      //   // this.eload.maximum = null;
      // }
    },

    validateAccountNumberRangeIfNeeded() {
      this.inputTypeHandler = null;

      if (this.eload.input_type !== "AN") return true;

      const min = parseInt(this.eload.minimum, 10);
      const max = parseInt(this.eload.maximum, 10);

      if (Number.isNaN(min) || Number.isNaN(max)) {
        this.inputTypeHandler = "Min and Max are required.";
        return false;
      }
      if (min >= max) {
        this.inputTypeHandler = "Min should be less than Max.";
        return false;
      }
      return true;
    },

    async handleSubmit() {
      if (this.promoId > 0 && !this.eloadPromoModule.access.update) {
        showAccessRestrictedModal();
        return;
      }

      this.submitted = true;
      this.$v.$touch();

      try {
        if (!this.validateAccountNumberRangeIfNeeded()) return;

        // validate only the fields that are active/editable
        if (this.$v.eload.$invalid) {
          return;
        }

        const status = this.eload.limitedStatus ? 2 : (this.eload.status ? 1 : 0);

        const promoAmountMode = this.promo_amount_mode || "default";
        const promoDetailsMode = this.details_mode || "default";

        const useDefaultMain = promoDetailsMode === "custom";

        const networkValueForMain = useDefaultMain ? (this.defaultDetails?.network ?? this.eload.network) : this.eload.network;
        const networkObj = (this.loadNetworkList || []).find(
          (n) => String(n.name || "").trim() === String(networkValueForMain || "").trim()
        );
        const networkIdForMain = networkObj?.id ?? null;

        const customNetworkObj = (this.loadNetworkList || []).find(
          (n) => String(n.name || "").trim() === String(this.eload.network || "").trim()
        );
        const networkIdForCustom = customNetworkObj?.id ?? null;

        const promoNameVal = ((useDefaultMain ? (this.defaultDetails?.name ?? this.eload.name) : this.eload.name) || "").trim();
        const promoCodeVal = ((useDefaultMain ? (this.defaultDetails?.code ?? this.eload.code) : this.eload.code) || "").trim();
        const promoDescriptionVal = ((useDefaultMain ? (this.defaultDetails?.description ?? this.eload.description) : this.eload.description) || "").trim();
        const promoValidityVal = ((useDefaultMain ? (this.defaultDetails?.validity ?? this.eload.validity) : this.eload.validity) || "").trim();
        const promoCategoryVal = ((useDefaultMain ? (this.defaultDetails?.category ?? this.eload.category) : this.eload.category) || "").trim();
        const promoCategoryIdsVal = (promoDetailsMode === 'custom')
          ? (Array.isArray(this.eload.categoryIds) ? this.eload.categoryIds : [])
          : (Array.isArray(this.defaultCategoryIds) ? this.defaultCategoryIds : []);
          

        const promoServiceIdVal = Number(useDefaultMain ? (this.defaultDetails?.service ?? this.eload.service) : (this.eload.service || 0));
        const promoInputTypeVal = ((useDefaultMain ? (this.defaultDetails?.input_type ?? this.eload.input_type) : this.eload.input_type) || "").trim();
        const promoMinCharVal = useDefaultMain ? (this.defaultDetails?.minimum == null ? null : Number(this.defaultDetails.minimum)) : (this.eload.minimum == null ? null : Number(this.eload.minimum));
        const promoMaxCharVal = useDefaultMain ? (this.defaultDetails?.maximum == null ? null : Number(this.defaultDetails.maximum)) : (this.eload.maximum == null ? null : Number(this.eload.maximum));

        const body = {
          id: Number(this.eload.promoId || 0),

          promoName: promoNameVal,
          promoCode: promoCodeVal,

          // main promo amount should always reflect the DB/default column
          promoAmount: Number(this.defaultDetails?.amount || this.eload.amount || 0),
          promoDescription: promoDescriptionVal,
          promoValidity: promoValidityVal,

          networkId: networkIdForMain,
          network: (networkValueForMain || "").trim(),

          promoCategory: promoCategoryVal,
          promoCategoryIds: promoCategoryIdsVal,

          promoServiceId: promoServiceIdVal,
          promoInputType: promoInputTypeVal,
          promoMinChar: promoMinCharVal,
          promoMaxChar: promoMaxCharVal,

          promoStatus: status,

          promoAmountMode,
          promoDetailsMode,
        };

        // custom amount column
        if (promoAmountMode === "custom") {
          body.customPromoAmount = Number(this.eload.amount || 0);
        } else {
          body.customPromoAmount = null;
        }

        // custom details json
        if (promoDetailsMode === "custom") {
          body.promoCustomDetails = {
            service_id: Number(this.eload.service || 0),
            // Use current selected network, not old customDetails value
            network_id: Number(networkIdForCustom ?? null),
            // category_ids: Array.isArray(this.eload.categoryIds) ? this.eload.categoryIds : [],

            promo_name: (this.eload.name || "").trim(),
            promo_code: (this.eload.code || "").trim(),
            input_type: (this.eload.input_type || "").trim(),
            min_char: this.eload.minimum == null ? null : Number(this.eload.minimum),
            max_char: this.eload.maximum == null ? null : Number(this.eload.maximum),
            validity: (this.eload.validity || "").trim(),
            description: (this.eload.description || "").trim(),
          };
        } else {
          body.promoCustomDetails = null;
        }

        if (console?.debug) console.debug("Submitting promo:", body);

        // Determine whether mode changed and prepare confirmation copy
        const modeChangingToDefault = (this.originalDetailsMode === 'custom' && promoDetailsMode === 'default');
        const modeChangingToCustom = (this.originalDetailsMode === 'default' && promoDetailsMode === 'custom');

        let swalOptions = {
          icon: 'warning',
          title: 'Confirm SKU Update',
          html: `Are you sure you want to update this SKU?<br><br>This change will not affect the E-Load Core Manager and will only apply to the Peddlr Lite Manager.`,
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          confirmButtonColor: '#804997',
          cancelButtonColor: '#d33',
          reverseButtons: false,
        };

        if (modeChangingToDefault) {
          swalOptions.icon = 'info';
          swalOptions.title = 'Switch to Default Details';
          swalOptions.html = `You're about to switch from Custom Details back to Default Details.<br><br>Are you sure you want to continue?`;
        } else if (modeChangingToCustom) {
          swalOptions.icon = 'warning';
          swalOptions.title = 'Switch to Custom Details';
          swalOptions.html = `You're about to switch from Default Details to Custom Details.<br><br>Are you sure you want to continue?`;
        }

        const confirmation = await Swal.fire(swalOptions);

        if (!confirmation.isConfirmed) {
          this.submitted = false;
          return;
        }

        await this.actionLoadPromo({ carrier, action: "update", body });

        this.submitted = false;
        // Success modal
        await Swal.fire({
          icon: 'success',
          title: 'Updated Successful!',
          text: 'E-Load SKU is successfully updated.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#804997'
        });

        this.$router.push(this.$route.query.redirectFrom || { name: "E-Load Lite SKU Manager" });
      } catch (error) {
        console.log(error);
        Swal.fire("Update Failed!", error?.message || String(error), "warning");
        this.submitted = false;
      }
    },
    async viewChangeLogModal(){
        this.changeLogModal = true;
        // Fetch logs when modal opens
        await this.fetchSkuFieldLogs();
    },
    openChangePreview(item) {
      if (this._changePreviewOutsideHandler) {
        document.removeEventListener('click', this._changePreviewOutsideHandler);
        this._changePreviewOutsideHandler = null;
      }

      // Store data and show preview
      this.changePreviewData = item || null;
      this.changePreviewVisible = true;

      // Setup click-outside handler with proper timing
      this.$nextTick(() => {
        setTimeout(() => {
          this._changePreviewOutsideHandler = (e) => {
            const panel = document.querySelector('.change-preview__inner');
            if (!panel || !this.changePreviewVisible) return;
            
            // Check if click is outside the panel
            if (!panel.contains(e.target)) {
              this.closeChangePreview();
            }
          };
          document.addEventListener('click', this._changePreviewOutsideHandler, true);
        }, 100);
      });
    },
    closeChangePreview() {
      this.changePreviewVisible = false;
      this.changePreviewData = null;
      
      // Remove click-outside listener
      if (this._changePreviewOutsideHandler) {
        document.removeEventListener('click', this._changePreviewOutsideHandler, true);
        this._changePreviewOutsideHandler = null;
      }
    },
    async fetchSkuFieldLogs() {

      // I dont know why but promoId from route params/query is sometimes undefined when page refresh but adding this fallback it fixes it
      const fallBackPromoId = Number(this.promoId || this.$route.params?.id || this.$route.query?.id || this.$route.query?.promoId) || 0;
      const params = {
        page: this.searchValue.page,
        rows: this.searchValue.rows,
        sort: this.sortBy || 'createdAt',
        orderDesc: this.sortDesc,
        promoId: fallBackPromoId
      };

      if (this.searchValue.search && this.searchValue.search.trim()) {
        params.search = this.searchValue.search.trim();
        params.searchField = this.searchValue.searchField || null;
      }

      // Add date range if present
      if (this.searchValue.dateRange) {
        params.dateRange = this.searchValue.dateRange;
      }

      // console.log('Fetching SKU logs with params:', params);

      try {
        await this.getSkuFieldLogs(params);
        // console.log('SKU logs loaded successfully:', response);
      } catch (error) {
        console.error('Error loading SKU logs:', error);
        this.$alertify?.error?.('Failed to load SKU logs');
      }
    },
    tableSorting(ctx) {
      // Update sort state
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      // console.log('Sorting by:', ctx.sortBy, 'desc:', ctx.sortDesc);
      // Refetch with new sort
      this.fetchSkuFieldLogs();
    },
  },

  beforeRouteLeave(to, from, next) {
    // // warn only when we're in editable modes
    // if (this.details_mode === "custom" || this.promo_amount_mode === "custom") {
    //   Swal.fire({
    //     title: "Leave this page?",
    //     text: "You have unsaved changes.",
    //     icon: "warning",
    //     showCancelButton: true,
    //     confirmButtonColor: "#804997",
    //     cancelButtonColor: "#d33",
    //     confirmButtonText: "Leave",
    //     cancelButtonText: "Stay",
    //   }).then((result) => {
    //     if (result.isConfirmed) next();
    //   });
    //   return;
    // }
    next();
  },

  beforeDestroy() {
    if (this._changePreviewOutsideHandler) {
      document.removeEventListener('click', this._changePreviewOutsideHandler, true);
      this._changePreviewOutsideHandler = null;
    }
  },
};
</script>

<template>
  <div class="row">
    <div class="col-lg-10">
      <div class="card eload-card">
        <div class="card-body">
          <form class="px-4 py-3" @submit.prevent="handleSubmit()">
            <div class="row">
              <label
                v-if="promoId > 0 && !eloadPromoModule.access.update"
                class="form-label medium fw-bold text-danger mb-3"
              >
                Note: Your access is limited. You cannot update a promo.
              </label>
            </div>

            <!-- Active -->
            <div class="row">
              <div class="col-12">
                <div class="form-check form-switch form-switch-lg mb-3" dir="ltr">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    id="eloadActiveSwitch"
                    v-model="eload.status"
                    @change="onToggleStatus()"
                    :disabled="promoId > 0 ? !eloadPromoModule.access.update : !eloadPromoModule.access.create"
                  />
                  <label class="form-check-label form-label fw-bold" for="eloadActiveSwitch">Active</label>
                </div>
              </div>
            </div>

            <!-- Temporary Schedule Note -->
            <div class="row mb-4" v-if="showScheduleNote">
              <div class="col-12">
                <div class="schedule-note">
                  <strong>Note:</strong>This SKU currently has an active schedule in Core. Once the schedule takes effect, the SKU will automatically switch to Default Details so the schedule is properly reflected in Peddlr Lite.
                </div>
              </div>
            </div>

            <!-- Promo ID -->
            <div class="row mb-2">
              <div class="col-12">
                <h6 class="mb-0">
                  Promo ID:
                  <span class="fw-normal">{{ promoId > 0 ?  promoId : "New" }}</span>
                </h6>
              </div>
            </div>

            <!-- Section 1: Amount -->
            <div class="section-card mt-3">
              <div class="section-card__head">
                <label class="form-label fw-bold mb-0">Details to Used<span v-if="promo_amount_mode === 'custom'" class="req">*</span></label>

                <div class="details-switch">
                  <button
                    type="button"
                    class="details-switch__btn"
                    :class="{ 'is-active': promo_amount_mode === 'default' }"
                    @click="setAmountMode('default')"
                  >
                    Default Details
                  </button>

                  <button
                    type="button"
                    class="details-switch__btn"
                    :class="{ 'is-active': promo_amount_mode === 'custom' }"
                    @click="setAmountMode('custom')"
                  >
                    Custom Details
                  </button>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-12">
                  <label class="form-label small fw-bold">Amount<span v-if="promo_amount_mode === 'custom'" class="req">*</span></label>
                  <input
                    type="number"
                    step="0.01"
                    class="form-control amount-input"
                    v-model="eload.amount"
                    placeholder="Enter Load Amount"
                    :readonly="promo_amount_mode === 'default'"
                    :disabled="promo_amount_mode === 'default'"
                    :class="{ 'is-invalid': submitted && $v.eload.amount.$error }"
                  />
                  <div v-if="submitted && !$v.eload.amount.required" class="invalid-feedback">
                    Load Amount is required.
                  </div>
                </div>
              </div>
            </div>

            <!-- Section 2: Details -->
            <div class="section-card mt-4">
              <div class="section-card__head">
                <label class="form-label fw-bold mb-0">Details to Used<span v-if="details_mode === 'custom'" class="req">*</span></label>

                <div class="details-switch">
                  <button
                    type="button"
                    class="details-switch__btn"
                    :class="{ 'is-active': details_mode === 'default' }"
                    @click="setDetailsMode('default')"
                  >
                    Default Details
                  </button>

                  <button
                    type="button"
                    class="details-switch__btn"
                    :class="{ 'is-active': details_mode === 'custom' }"
                    @click="setDetailsMode('custom')"
                  >
                    Custom Details
                  </button>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-md-6">
                  <label class="form-label small fw-bold">Service<span v-if="details_mode === 'custom'" class="req">*</span></label>
                  <b-form-select
                    v-model="eload.service"
                    :options="eloadServiceOptions"
                    :readonly="details_mode === 'default'"
                    :disabled="details_mode === 'default'"
                    class="form-select mb-2 shadow-none"
                    :class="{ 'is-invalid': submitted && $v.eload.service.$error }"
                  >
                    <template #first>
                      <b-form-select-option :value="null" disabled>Select Service</b-form-select-option>
                    </template>
                  </b-form-select>
                  <div v-if="submitted && $v.eload.service.$error" class="text-danger">
                    <span v-if="!$v.eload.service.required">This value is required.</span>
                  </div>
                </div>

                <div class="col-md-6">
                  <label class="form-label small fw-bold">Network<span v-if="details_mode === 'custom'" class="req">*</span></label>
                  <b-form-select
                    v-model="eload.network"
                    :options="networkOptions"
                    :readonly="details_mode === 'default'"
                    :disabled="details_mode === 'default'"
                    class="form-select mb-2 shadow-none"
                    :class="{ 'is-invalid': submitted && $v.eload.network.$error }"
                    @change="onNetworkChange"
                  >
                    <template #first>
                      <b-form-select-option :value="null" disabled>Select Network</b-form-select-option>
                    </template>
                  </b-form-select>
                  <div v-if="submitted && $v.eload.network.$error" class="text-danger">
                    <span v-if="!$v.eload.network.required">This value is required.</span>
                  </div>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-12">
                  <label class="form-label small fw-bold">Category<span v-if="details_mode === 'custom'" class="req">*</span></label>
                  <multiselect
                    :multiple="true"
                    v-model="selectedCategory"
                    :options="categoryList"
                    track-by="id"
                    label="text"
                    :readonly="details_mode === 'default'"
                    :disabled="details_mode === 'default'"
                    placeholder="Select Category"
                    :close-on-select="false"
                    :hide-selected="true"
                    :class="{ 'is-invalid': submitted && $v.eload.category.$error }"
                  >
                    <template #option="{ option }">
                      <div class="d-flex align-items-center option-with-sc">
                        <span>{{ option.origName || option.text }}</span>
                        <span v-if="option.isSpecial" class="sc-label">(SC)</span>
                      </div>
                    </template>

                    <template #tag="{ option, remove }">
                      <span class="multiselect__tag">
                        <span class="multiselect__tag__content">{{ option.origName || option.text }}</span>
                        <span v-if="option.isSpecial" class="sc-label">(SC)</span>
                        <span class="multiselect__tag__remove" @click.stop="remove(option)">&times;</span>
                      </span>
                    </template>
                  </multiselect>
                  <div v-if="submitted && $v.eload.category.$error" class="text-danger">
                    Category is required.
                  </div>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-md-6">
                  <label class="form-label small fw-bold">Promo Name<span v-if="details_mode === 'custom'" class="req">*</span></label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="eload.name"
                    placeholder="Enter Promo Name"
                    :readonly="details_mode === 'default'"
                    :disabled="details_mode === 'default'"
                    :class="{ 'is-invalid': submitted && $v.eload.name.$error }"
                  />
                  <div v-if="submitted && !$v.eload.name.required" class="invalid-feedback">
                    Promo Name is required.
                  </div>
                </div>

                <div class="col-md-6">
                  <label class="form-label small fw-bold">Promo Code<span v-if="details_mode === 'custom'" class="req">*</span></label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="eload.code"
                    placeholder="Enter Promo Code"
                    :readonly="details_mode === 'default'"
                    :disabled="details_mode === 'default'"
                    :class="{ 'is-invalid': submitted && $v.eload.code.$error }"
                  />
                  <div v-if="submitted && !$v.eload.code.required" class="invalid-feedback">
                    Promo Code is required.
                  </div>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-12">
                  <label class="form-label small fw-bold">Input Type<span v-if="details_mode === 'custom'" class="req">*</span></label>
                  <b-form-select
                    v-model="eload.input_type"
                    :options="inputTypeList"
                    :readonly="details_mode === 'default'"
                    :disabled="details_mode === 'default'"
                    class="form-select shadow-none"
                    @change="getSelectedInputType"
                    :class="{ 'is-invalid': submitted && $v.eload.input_type.$error }"
                  />
                  <div v-if="submitted && !$v.eload.input_type.required" class="invalid-feedback d-block">
                    Input Type is required.
                  </div>
                </div>

                <div class="col-md-6 mt-2">
                  <input
                    type="number"
                    class="form-control"
                    v-model="eload.minimum"
                    placeholder="Min Number"
                    :readonly="details_mode === 'default' || eload.input_type === 'MN' || eload.input_type == null"
                    :disabled="details_mode === 'default' || eload.input_type === 'MN' || eload.input_type == null"
                  />
                  <div v-if="submitted && inputTypeHandler != null" class="invalid-feedback">
                    {{ inputTypeHandler }}
                  </div>
                </div>

                <div class="col-md-6 mt-2">
                  <input
                    type="number"
                    class="form-control"
                    v-model="eload.maximum"
                    placeholder="Max Number"
                    :readonly="details_mode === 'default' || eload.input_type === 'MN' || eload.input_type == null"
                    :disabled="details_mode === 'default' || eload.input_type === 'MN' || eload.input_type == null"
                    :class="{ 'is-invalid': submitted && inputTypeHandler != null }"
                  />
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-12">
                  <label class="form-label small fw-bold">Validity<span v-if="details_mode === 'custom'" class="req">*</span></label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="eload.validity"
                    placeholder="Valid for 365 days"
                    :disabled="details_mode === 'default'"
                    :class="{ 'is-invalid': submitted && $v.eload.validity.$error }"
                  />
                  <div v-if="submitted && !$v.eload.validity.required" class="invalid-feedback">
                    Validity is required.
                  </div>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-12">
                  <label class="form-label small fw-bold">Description<span v-if="details_mode === 'custom'"  class="req">*</span></label>
                  <b-form-textarea
                    v-model="eload.description"
                    placeholder="Enter promo description..."
                    :readonly="details_mode === 'default'"
                    :disabled="details_mode === 'default'"
                    rows="4"
                    max-rows="8"
                    :class="{ 'is-invalid': submitted && $v.eload.description.$error }"
                  />
                  <div v-if="submitted && !$v.eload.description.required" class="invalid-feedback d-block">
                    Description is required.
                  </div>
                </div>
              </div>
            </div>
            <p v-if="promoId > 0" class="mt-2">Change Log: <a href="#" @click="viewChangeLogModal" class="text-decoration-underline">View</a></p>

            <div class="text-end mt-4">
              <b-button
                :disabled="promoId > 0 ? !eloadPromoModule.access.update : !eloadPromoModule.access.create"
                variant="primary"
                class="w-sm btn-primary-elevated"
                type="submit"
              >
                Update
              </b-button>

              <router-link :to="{ name: 'E-Load Lite SKU Manager', params: {} }" class="tc-white">
                <b-button variant="light" class="w-sm ms-2" type="button">Cancel</b-button>
              </router-link>

              <p v-if="eload.error" class="text-danger mt-2 mb-0">{{ eload.error }}</p>
            </div>
          </form>
          <!-- Change log modal -->
          <b-modal
            id="changeLog"
            size="lg"
            centered
            hide-header-close
            v-model="changeLogModal"
            modal-class="change-log-modal"
          >
            <template #modal-title>
              E-Load SKU Logs
            </template>
            <template #modal-footer>
              <div class="w-100 text-end">
                <b-button variant="light" class="px-5" @click="changeLogModal = false">Close</b-button>
              </div>
            </template>
            <div class="row align-items-start g-0 mt-2 mb-3">
              <div class="col-sm-2">
                <b-form-group label-for="search-type" class="text-end">
                  <b-form-select
                    v-model="searchValue.searchField"
                    class="form-select rounded-0 shadow-none w-100"
                    :options="searchLogOption"
                  >
                  </b-form-select>
                </b-form-group>
              </div>

              <div class="col-sm-3">
                <b-form-group label-for="search-keyword" description="Search Keyword" class="text-end">
                  <input
                    class="form-control rounded-0 shadow-none w-100"
                    v-model="searchValue.search"
                    placeholder="Search Keyword"
                    @keydown.enter.prevent="fetchSkuFieldLogs"
                  />
                </b-form-group>
              </div>

              <div class="col-sm-3 ms-3">
                <b-form-group label-for="date-range" description="Filter by date" class="text-end">
                  <flat-pickr
                    v-model="searchValue.dateRange"
                    :config="rangeOptions"
                    placeholder="Select date range"
                    class="form-control rounded-0 shadow-none w-100"
                  ></flat-pickr>
                </b-form-group>
              </div>

              <div class="col d-flex justify-content-end align-items-center buttons-group gap-1 flex-wrap">
                <b-button variant="primary" type="button" @click="fetchSkuFieldLogs" class="px-3 d-flex align-items-center">
                  <i class='bx bx-search bx-fw me-2'></i>
                  Search
                </b-button>

                <b-button variant="primary" @click="onExport" type="button" class="px-3 d-flex align-items-center">
                  <i class='uil uil-export me-2'></i>
                  Export
                </b-button>
              </div>
            </div>
            <div class="row">
                <div class="col-sm-12">
                  <!-- table responsive -->
                  <div class="table-responsive">
                    <div class="table align-middle table-nowrap">
                      <b-table
                        :items="skuFieldLogs"
                        :fields="logFields"
                        responsive="sm"
                        :current-page="searchValue.page"
                        :sort-by.sync="sortBy"
                        :no-local-sorting="true"
                        :sort-desc.sync="sortDesc"
                        @sort-changed="tableSorting"
                      >
                        <template #cell(details)="data">
                          <a href="#" class="view-link" @click.prevent="openChangePreview(data.item, $event)">View changes</a>
                        </template>
                        <template #cell(created_at)="data">
                          <span>{{ data.item.created_at | customFormatDate }}</span>
                        </template>
                      </b-table>
                    </div>
                  </div>
                  <!-- change preview floating panel -->
                  <div v-if="changePreviewVisible" class="change-preview">
                    <div class="change-preview__inner">
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <h6 class="mb-0 text-white fw-bold">Edited Fields:</h6>
                        <button class="btn-close" @click="closeChangePreview">×</button>
                      </div>

                      <div v-if="changePreviewData && changePreviewData.changes && changePreviewData.changes.length > 0">
                        <table class="table table-sm table-change mb-0">
                          <thead>
                            <tr>
                              <th style="width: 35%">Field</th>
                              <th style="width: 65%">Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(c, idx) in changePreviewData.changes" :key="idx">
                              <!-- <td>{{ c.field }}</td>
                              <td>{{ c.old }} → {{ c.new }}</td> -->
                              <td class="align-top text-white">{{ c.field }}</td>
                              <td>
                                <div class="before-line text-white">
                                  <!-- <strong>Before:</strong> -->
                                   Before:
                                  <span class="ms-1">{{ Array.isArray(c.old) ? c.old.join(', ') : (c.old === null || c.old === undefined ? '-' : c.old) }}</span>
                                </div>
                                <div class="after-line text-white mt-2">
                                  <!-- <strong>After:</strong> -->
                                   After:
                                  <span class="ms-1">{{ Array.isArray(c.new) ? c.new.join(', ') : (c.new === null || c.new === undefined ? '-' : c.new) }}</span>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div v-else class="text-white text-center py-3">
                        <p class="mb-0">No detailed changes available.</p>
                      </div>
                    </div>
                  </div>
                  <div class="row g-0 text-center" v-if="skuFieldLogsTotal == 0">
                    <span>No data to display.</span>
                  </div>
                  <div class="row g-0 align-items-center" v-if="skuFieldLogsTotal > 0">
                    <div class="col-auto">
                      <div class="d-flex align-items-center">
                        <label class="me-2 mb-0 col-form-label">Display</label>
                        <b-form-select v-model="searchValue.rows" :options="pageOptions" class="form-select shadow-none w-fit" @change="fetchSkuFieldLogs"></b-form-select>
                      </div>
                    </div>
                    <div class="col" v-if="skuFieldLogsTotal > searchValue.rows">
                      <div class="float-end">
                        <b-pagination
                          class="pagination-rounded mb-0"
                          v-model="searchValue.page"
                          @input="fetchSkuFieldLogs"
                          :total-rows="skuFieldLogsTotal"
                          :per-page="searchValue.rows"
                        ></b-pagination>
                      </div>
                    </div>
                  </div>
                </div><!--end of col-12-->
            </div><!--end of row-->
          </b-modal>

        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.eload-card {
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.9);
}

.req {
  color: #ef4444;
  margin-left: 2px;
}

.section-card {
  border-radius: 14px;
  border: 1px solid #edf0f5;
  background: #ffffff;
  padding: 18px 18px;
}

.section-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

::v-deep .details-switch {
  display: grid;                
  grid-template-columns: 1fr 1fr;
  border-radius: 10px;
  border: 2px solid #e935a1;
  background: #ffffff;
  overflow: hidden;
}

::v-deep .details-switch__btn {
  appearance: none;
  cursor: pointer;
  padding: 4px 10px;
  border: 0;
  background: transparent;
  color: var(--neutrals-neutral-400, #AFAFAF);
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; 
  white-space: nowrap;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0;
}

::v-deep .details-switch__btn.is-active {
  background: linear-gradient(135deg, #e935a1 0%, #d946a6 100%);
  color: var(--White, #FFF);
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; 
  box-shadow: 0 8px 18px rgba(217, 70, 166, 0.18);
}

::v-deep .details-switch__btn:focus {
  outline: none;
}

::v-deep .multiselect__tags {
  border-radius: 10px !important;
  border-color: #e5e7eb !important;
  min-height: 44px;
  padding-top: 8px;
}

  ::v-deep .multiselect.is-invalid .multiselect__tags {
  padding-right: calc(1.5em + 0.94rem) !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23f34e4e'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23f34e4e' stroke='none'/%3e%3c/svg%3e") !important;
  background-repeat: no-repeat !important;
  background-position: right calc(1.875em + 0.235rem) center !important;
  background-size: calc(0.75em + 0.47rem) calc(0.75em + 0.47rem) !important;
  border-color: #f34e4e !important;
}

::v-deep .multiselect--disabled .multiselect__tags {
  background: #f8fafc;
}

::v-deep .multiselect__tag {
  background: #d946a6 !important; /* darker pink */
  color: #ffffff !important;
  border: none !important;
  display: inline-flex !important;
  align-items: center !important;
  padding: 4px 6px !important;
  gap: 6px !important;
}

/* remove icon: white X, transparent background */
::v-deep .multiselect__tag__remove {
  color: #ffffff !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  padding: 0 4px !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
}

::v-deep .multiselect__tag__remove svg {
  fill: #ffffff !important;
}

/* ensure remove icon has no green square: make transparent by default, darker pink on hover */
::v-deep .multiselect__tag .multiselect__tag-icon,
::v-deep .multiselect__tag .multiselect__tag-icon::after {
  background: transparent !important;
  color: #ffffff !important;
  box-shadow: none !important;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
}

::v-deep .multiselect__tag__remove svg,
::v-deep .multiselect__tag .multiselect__tag-icon svg {
  fill: #ffffff !important;
}

::v-deep .multiselect__tag:hover .multiselect__tag__remove,
::v-deep .multiselect__tag:hover .multiselect__tag-icon {
  background: #c0337a !important; 
}

::v-deep .sc-label {
  color: #AFAFAF !important;
}

::v-deep .option-with-sc { display:flex; align-items:center; gap:6px; }

::v-deep .multiselect__option--selected,
::v-deep .multiselect__option--highlight {
  color: #ffffff !important;
}

::v-deep .multiselect__option--selected .sc-label,
::v-deep .multiselect__option--highlight .sc-label {
  color: #ffffff !important;
}

::v-deep .multiselect__tag .sc-label {
  color: #ffffff !important;
}


::v-deep .multiselect--disabled .multiselect__tags {
  background: #f5f6f8;
}
::v-deep .multiselect--disabled .multiselect__select {
  background: #f5f6f8;
}

.schedule-note {
  border-radius: 8px;
  background: #f6eefb; /* light purple */
  border: 1px solid #d6b8e6;
  padding: 4px 6px;
  color: #1f1f1f;
  font-size: 14px;
  font-style: normal;
  line-height: 150%;
}

.schedule-note strong {
  margin-right: 6px;
}

.change-preview {
  position: fixed;
  left: 50%;
  top: 18%;
  transform: translateX(-50%);
  z-index: 2050;
  background: transparent;
  padding: 0;
  pointer-events: none;
}

  .change-preview__inner {
  background: #0f1112 !important;
  border-radius: 12px;
  padding: 22px 26px;
  width: 720px;
  max-width: 94vw;
  max-height: 70vh;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.04);
  pointer-events: auto;
}



.table-change thead th {
  background: #0f1112 !important;
  color: #ffffff !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.913);
  font-weight: 700;
  font-size: 14px;
  padding: 10px 12px;
  text-align: left;
}

.table-change td {
  background: #0f1112 !important;
  color: #e6e6e6 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.654);
  padding: 11px 12px;
  font-size: 13px;
  line-height: 1.4;
}
.table-change tbody tr:last-child td {
  border-bottom: none;
}

.table-change tbody tr:hover {
  background: rgba(255, 255, 255, 0.02);
}

.change-preview__inner .table-change td {
  padding: 8px 18px;
  vertical-align: top;
}
.change-preview__inner .table-change thead th {
  padding: 12px 18px;
  border-bottom: 1px solid rgba(255,255,255,0.12);
}
.change-preview__inner .before-line strong,
.change-preview__inner .after-line strong {
  color: #e6e6e6;
  /* font-weight: 600; */
}

.btn-close {
  background: transparent;
  border: 0;
  color: #ffffff;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.12s;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close:hover { opacity: 1; }

::v-deep .modal-content {
  border-radius: 12px;
  padding: 22px 24px;
}

/* Responsive tweaks specific to the change log modal */
.change-log-modal .modal-dialog {
  max-width: 900px;
  width: 100%;
}

@media (max-width: 992px) {
  .change-log-modal .modal-dialog { max-width: 720px; }
}

@media (max-width: 768px) {
  .change-log-modal .modal-dialog { max-width: 95vw; margin: 0.5rem; }
  .change-log-modal .modal-content { padding: 14px; }
}

@media (max-width: 576px) {
  /* Stack the controls vertically and make them full width */
  .change-log-modal .row.align-items-start.g-0 > .col-sm-2,
  .change-log-modal .row.align-items-start.g-0 > .col-sm-3,
  .change-log-modal .row.align-items-start.g-0 > .col-sm-1 {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }

  /* Make form groups left-aligned on small screens */
  .change-log-modal .b-form-group.text-end {
    text-align: left !important;
  }

  /* Remove left margins added for desktop spacing */
  .change-log-modal .ms-3 { margin-left: 0 !important; }

  /* Buttons group: keep inline, right-aligned, and wrap when needed */
  .change-log-modal .buttons-group {
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .change-log-modal .buttons-group .btn {
    min-width: 110px;
  }
}

.logs-controls {
  gap: 8px;
  flex-wrap: wrap;
}

.logs-controls .form-control,
.logs-controls .form-select {
  height: 40px;
  border-radius: 8px;
}

.view-link {
  color: #e935a1;
  font-weight: 600;
  text-decoration: underline !important;
}
.view-link:hover {
  text-decoration: underline;
}

/* table header and rows */
::v-deep .table thead th {
  font-weight: 600;
  color: #111827;
}
::v-deep .table tbody td {
  color: #111827;
  vertical-align: middle;
}

/* make pagination dots purple */
::v-deep .pagination-rounded .page-item.active .page-link {
  background: #804997;
  border-color: #804997;
}

::v-deep .btn-outline-primary:hover,
::v-deep .btn-export-logs:hover {
  background: #e935a1;
  border-color: #e935a1;
  color: #ffffff;
}

::v-deep .modal-footer {
  border-top: none !important;
  padding-top: 0.2rem !important;
  padding-bottom: 0.2rem !important;
}

</style>
